import { HolisticCategory } from '@fruitful/types';
import { router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import { Keyboard, Pressable } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    DEFAULT_COLOR,
    DEFAULT_ICON,
    FullScreenSpinner,
    isWeb,
    Sheet,
    useToastController,
    YStack,
} from 'smoothie';

import { isEmpty } from '../../../../common/misc';
import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../../../logger/log-rocket/LogRocketFacade';
import { captureException, captureMessage } from '../../../../logger/Logger';
import { useUser } from '../../../../user/providers/UserProvider';
import useAcceptJointInvite from '../../../hooks/useAcceptJointInvite';
import useCreateSpace from '../../../hooks/useCreateSpace';
import useGetAllBankAccountDetails from '../../../hooks/useGetAllBankAccountDetails';
import useGetBankAccountDetails from '../../../hooks/useGetBankAccountDetails';
import { useGetPendingJointInvites } from '../../../hooks/useGetJointInvites';
import { preselectedAccountForCardAtom } from '../../../state/SpacesAtoms';
import { preselectedTransferAccountsAtom } from '../../../state/TransferAtoms';
import {
    AccountType,
    DEFAULT_INDIVIDUAL_CHECKING_ACCOUNT_NICKNAME,
    DEFAULT_JOINT_CHECKING_ACCOUNT_NICKNAME,
} from '../../../types/BankAccount';
import {
    FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
    getBankingDisclosures,
} from '../../Disclosures';
import SpaceOwnershipTabs, { AccountCreatedStatus } from './SpaceOwnershipTabs';

const SpaceOwnership = () => {
    LogRocketFacade.tagPage('SpaceOwnership');

    const user = useAuthedUser();
    const { refetch: refetchUser } = useUser();

    const toast = useToastController();

    const { fromKyc, jointInviteBankAccountId } = useLocalSearchParams<{
        fromKyc?: string;
        jointInviteBankAccountId?: string;
    }>();
    const isFromKyc = fromKyc === 'true';

    const setPreselectedTransferAccount = useSetAtom(
        preselectedTransferAccountsAtom,
    );
    const setPreselectedAccountForCard = useSetAtom(
        preselectedAccountForCardAtom,
    );

    const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
    const [accountCreatedStatus, setAccountCreatedStatus] =
        useState<AccountCreatedStatus>(AccountCreatedStatus.NotCreated);
    const [isAcceptingJointInvite, setIsAcceptingJointInvite] = useState(false);

    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    // Explicitly fetch joint account, it will not show up in aggregated account details until refetch loads after user has accepted invite
    const { accountDetails: jointAccount, isLoading: isLoadingJointAccount } =
        useGetBankAccountDetails(
            jointInviteBankAccountId as string,
            jointInviteBankAccountId != null,
        );

    const { mutateAsync: createSpace, isPending: isCreateSavingsSpacePending } =
        useCreateSpace();
    const { mutateAsync: acceptJointInvite } = useAcceptJointInvite();
    const { refetch } = useGetPendingJointInvites();

    const openAccount = async (jointEmail?: string): Promise<void> => {
        try {
            const createdAccount = await createSpace({
                body: {
                    userId: user.id,
                    accountType: AccountType.CHECKING,
                    holisticCategory: HolisticCategory.SAVE, // Default new Cash accounts to SAVE. This will be updated when someone clicks through account customization screens
                    nickname: !isEmpty(jointEmail)
                        ? DEFAULT_JOINT_CHECKING_ACCOUNT_NICKNAME
                        : DEFAULT_INDIVIDUAL_CHECKING_ACCOUNT_NICKNAME,
                    icon: DEFAULT_ICON,
                    color: DEFAULT_COLOR,
                    ...(!isEmpty(jointEmail) && { jointEmail }),
                    ...(!isFromKyc && {
                        disclosures: getBankingDisclosures({
                            policyTypes:
                                FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                        }),
                    }),
                },
            });

            setPreselectedTransferAccount({
                toSyncteraAccountId: createdAccount.syncteraAccountId,
            });
            setPreselectedAccountForCard({
                ...createdAccount,
            });
            setAccountCreatedStatus(
                jointEmail != null
                    ? AccountCreatedStatus.JointCreated
                    : AccountCreatedStatus.IndividualCreated,
            );

            void refetchUser?.();
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            router.replace('/home/banking');
            toast.show('Something went wrong creating your account');
        }
    };

    const onContinueFromAccountOpenSuccess = () => {
        // If coming from KYC, go to plaid linking screen first before transfer screen
        if (isFromKyc) {
            router.navigate('/banking/onboarding/first-funding');
        } else {
            router.navigate({
                pathname: '/banking/transfer',
                params: { context: 'firstFunding' },
            });
        }
    };

    const acceptJointAccountInvitation = async (): Promise<void> => {
        setIsAcceptingJointInvite(true);

        if (jointInviteBankAccountId == null) {
            captureMessage(
                'Missing jointInviteBankAccountId for accepting joint invitation',
            );
            router.navigate('/home/banking');
            return;
        }

        try {
            await acceptJointInvite(
                {
                    body: {
                        userId: user.id,
                        bankAccountId: jointInviteBankAccountId,
                        disclosures: getBankingDisclosures({
                            policyTypes:
                                FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                        }),
                    },
                },
                {
                    onSettled: async () => {
                        await refetch();
                    },
                },
            );

            setAccountCreatedStatus(AccountCreatedStatus.JointCreated);

            void refetchUser?.();
            void refetchAccountDetails();
        } catch (e) {
            captureException(e);
            router.replace('/home/banking');
            toast.show('Something went wrong');
        } finally {
            setIsAcceptingJointInvite(false);
        }
    };

    const onContinueFromAccountOpenSuccessJoint = () => {
        if (jointAccount?.hasCard) {
            // Since primary account holder has added cards to the account, send the joint account holder to also issue cards
            setPreselectedAccountForCard(jointAccount);

            router.navigate({
                pathname: '/banking/onboarding/add-card',
                params: {
                    isJointInvitation: 'true',
                    isFromJointAccountOpening: 'true',
                },
            });
        } else {
            router.navigate('/home/banking');
            toast.show('Success!');
        }
    };

    const isLoading =
        isCreateSavingsSpacePending ||
        isLoadingJointAccount ||
        isAcceptingJointInvite;

    if (isLoading) {
        return <FullScreenSpinner />;
    }

    return (
        <Pressable
            onPress={Keyboard.dismiss}
            style={{ flex: 1 }}
            disabled={isWeb}
        >
            <SpaceOwnershipTabs
                user={user}
                fromKyc={isFromKyc}
                accountCreated={accountCreatedStatus}
                onSubmitCreateAccount={openAccount}
                onInfoButtonPress={() => {
                    setIsInfoSheetOpen(true);
                }}
                isJointInvitation={jointInviteBankAccountId != null}
                onAcceptJointInvitation={acceptJointAccountInvitation}
                onContinueFromAccountOpenSuccess={
                    onContinueFromAccountOpenSuccess
                }
                onContinueFromAccountOpenSuccessJoint={
                    onContinueFromAccountOpenSuccessJoint
                }
            />
            <InfoSheet
                isOpen={isInfoSheetOpen}
                onClose={() => {
                    setIsInfoSheetOpen(false);
                }}
            />
        </Pressable>
    );
};

interface InfoScreenProps {
    isOpen: boolean;
    onClose: () => void;
}

const InfoSheet = ({ isOpen, onClose }: InfoScreenProps) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Account permissions"
            showCloseButton
        >
            <YStack marginTop={16} $sm={{ width: 400, alignSelf: 'center' }}>
                <BodyLargeEmphasized marginTop={16}>
                    Individual accounts
                </BodyLargeEmphasized>
                <BodyMediumRegular color="$gray1100" marginTop={8}>
                    You&apos;re the only one with access to the account.
                </BodyMediumRegular>
                <BodyLargeEmphasized marginTop={16}>
                    Joint accounts
                </BodyLargeEmphasized>
                <BodyMediumRegular color="$gray1100" marginTop={8}>
                    You&apos;ll invite another person to become a joint account
                    owner. Once we verify their identity, they&apos;ll have
                    access to the account, including transaction details,
                    account numbers, and the ability to transfer money in and
                    out.
                </BodyMediumRegular>
            </YStack>
        </Sheet>
    );
};

export default SpaceOwnership;
